import {
  AwaitContainer,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Combobox,
  Datepicker,
  Input,
  OptionValue,
  Table,
  TableSelectionState,
} from '@ibsys/kit';
import { FC, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  C1BotCandidateItem,
  C1BotCandidateListParams,
  useC1BotCandidates,
  useC1BotLoads,
  useStartC1BotLoad,
} from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';
import { CONVERSATION_PIPELINE_OPTIONS, type ConversationPipeline } from '@/entities/search';
import { toNumberOrUndefined } from '@/shared/lib/utils/number';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';

import { useC1BotCandidateColumns } from './useC1BotCandidateColumns';
import { useC1BotLoadColumns } from './useC1BotLoadColumns';

const PER_PAGE = 20;
// Значение селектора для варианта «не запускать обработку»: в запрос уходит null
const NO_PIPELINE = 'none';
const DEFAULT_PIPELINE: ConversationPipeline = 'raw_asr_process_pipeline_no_llm';
// Максимум кандидатов в выборке по умолчанию; пользователь может задать больше или меньше
const DEFAULT_C1_BOT_LIMIT = 500;
// Меньше одного разговора запрашивать нечего
const MIN_C1_BOT_LIMIT = 1;
// Верхняя граница совпадает с ограничением бэка на размер выборки
const MAX_C1_BOT_LIMIT = 10000;

// Интервал по времени начала разговора; обе границы календарь отдаёт как ISO-строку со временем
type C1BotDateRange = {
  from?: string;
  to?: string;
};

// Форма отбора кандидатов: интервал по времени начала разговора и ограничение количества
type C1BotCandidatesForm = {
  dateRange?: C1BotDateRange;
  limit?: number;
};

/**
 * Блок карточки интеграции c1_bot: отбор разговоров голосового бота по интервалу
 * времени начала (список кандидатов без загрузки), запуск загрузки отмеченных
 * разговоров с выбором обработки по каждому загруженному диалогу и журнал
 * выполненных загрузок.
 */
export const C1BotSection: FC = () => {
  const { t } = useTranslation(['integrations', 'common']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');
  const [page, setPage] = useState(1);
  // Параметры активного отбора кандидатов; undefined — отбор ещё не запускали
  const [activeParams, setActiveParams] = useState<C1BotCandidateListParams | undefined>(undefined);
  // Отмеченные к загрузке кандидаты (id разговоров источника)
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  // Обработка, запускаемая по каждому загруженному диалогу
  const [pipeline, setPipeline] = useState<ConversationPipeline | typeof NO_PIPELINE>(DEFAULT_PIPELINE);

  const { control, handleSubmit, watch } = useForm<C1BotCandidatesForm>({
    defaultValues: { dateRange: undefined, limit: DEFAULT_C1_BOT_LIMIT },
  });

  const { mutateAsync: startLoad, isPending: isStarting } = useStartC1BotLoad();
  const {
    data: candidatesData,
    isLoading: isLoadingCandidates,
    isFetching: isFetchingCandidates,
  } = useC1BotCandidates(activeParams);
  const { data, isLoading, isFetching } = useC1BotLoads({
    limit: PER_PAGE,
    offset: (page - 1) * PER_PAGE,
  });

  const candidateColumns = useC1BotCandidateColumns();
  const journalColumns = useC1BotLoadColumns();

  // Отбор кандидатов: показать разговоры за интервал, ничего не загружая
  const handleShowCandidates = useCallback((values: C1BotCandidatesForm) => {
    const dateFrom = values.dateRange?.from;
    const dateTo = values.dateRange?.to;
    const limit = toNumberOrUndefined(values.limit);
    // Интервал обязателен: без него отбор выгреб бы весь источник целиком
    if (!dateFrom || !dateTo || limit === undefined) return;

    setSelectedKeys([]);
    setActiveParams({ dateFrom, dateTo, limit });
  }, []);

  const handleSelectCandidates = useCallback((selection: TableSelectionState<C1BotCandidateItem>) => {
    setSelectedKeys(selection.selectedRows.map((row) => row.sourceId));
  }, []);

  // Запуск загрузки: пайплайн грузит только отмеченные разговоры и запускает по
  // каждому загруженному диалогу выбранную обработку
  const handleLoadSelected = useCallback(async () => {
    if (selectedKeys.length === 0) return;

    try {
      await startLoad({
        sourceIds: selectedKeys,
        pipeline: pipeline === NO_PIPELINE ? null : pipeline,
      });
      setSelectedKeys([]);
      setPage(1);
      toastSuccess(t('integrations:c1Bot.startSuccess'));
    } catch {
      toastError(t('integrations:c1Bot.startError'));
    }
  }, [selectedKeys, pipeline, startLoad, t]);

  const handleChangePipeline = useCallback((value?: OptionValue) => {
    // Сброс выбора селектор не предлагает: пустое значение оставляет обработку прежней
    if (value === undefined) return;
    setPipeline(value as ConversationPipeline | typeof NO_PIPELINE);
  }, []);

  const handleChangePagination = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  // Перечень обработок — общий с массовыми действиями над диалогами, плюс отказ от обработки
  const pipelineOptions = useMemo(
    () => [
      ...CONVERSATION_PIPELINE_OPTIONS.map((option) => ({ value: option.value, label: t(option.labelKey) })),
      { value: NO_PIPELINE, label: t('integrations:c1Bot.candidates.pipelineNone') },
    ],
    [t],
  );

  const candidates = candidatesData?.items ?? [];
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  // Отбор доступен только при полностью заданном интервале и корректном лимите;
  // границы лимита проверяет валидация поля, а не блокировка кнопки
  const formValues = watch();
  const canSearch =
    Boolean(formValues.dateRange?.from) &&
    Boolean(formValues.dateRange?.to) &&
    toNumberOrUndefined(formValues.limit) !== undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">{t('integrations:c1Bot.title')}</CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit(handleShowCandidates)}>
        <CardContent className="flex flex-col gap-3">
          <p className="text-muted-foreground text-sm">{t('integrations:c1Bot.description')}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Datepicker
              mode="range"
              showTimeInputs
              control={control}
              name="dateRange"
              label={t('integrations:c1Bot.filter.dateRange')}
            />
            <Input
              control={control}
              name="limit"
              type="number"
              label={t('integrations:c1Bot.filter.limit')}
              description={t('integrations:c1Bot.filter.limitDescription')}
              rules={{
                required: { value: true, message: t('common:required') },
                min: { value: MIN_C1_BOT_LIMIT, message: t('integrations:c1Bot.filter.limitMin') },
                max: { value: MAX_C1_BOT_LIMIT, message: t('integrations:c1Bot.filter.limitMax') },
              }}
            />
          </div>
        </CardContent>
        <CardFooter className="flex flex-wrap gap-3">
          <Button type="submit" disabled={!canAdmin || isFetchingCandidates || !canSearch}>
            {t('integrations:c1Bot.filter.showCandidates')}
          </Button>
        </CardFooter>
      </form>

      <CardContent className="flex flex-col gap-3">
        {!activeParams ? (
          <div className="text-muted-foreground text-sm p-2">{t('integrations:c1Bot.candidates.needInterval')}</div>
        ) : (
          <AwaitContainer isLoading={isLoadingCandidates || isFetchingCandidates}>
            {candidates.length === 0 ? (
              <div className="text-muted-foreground p-2">{t('integrations:c1Bot.candidates.empty')}</div>
            ) : (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-muted-foreground text-sm">
                    {t('integrations:c1Bot.candidates.selected', { count: selectedKeys.length })}
                  </span>
                  <div className="flex flex-wrap items-end gap-3">
                    <Combobox
                      options={pipelineOptions}
                      value={pipeline}
                      onChange={handleChangePipeline}
                      label={t('integrations:c1Bot.candidates.pipeline')}
                      isSearchable={false}
                      disabled={!canAdmin || isStarting}
                      className="w-72"
                      size="sm"
                    />
                    <Button
                      onClick={handleLoadSelected}
                      disabled={!canAdmin || isStarting || selectedKeys.length === 0}
                    >
                      {t('integrations:c1Bot.candidates.loadSelected')}
                    </Button>
                  </div>
                </div>
                <Table
                  type="simple"
                  data={candidates}
                  columns={candidateColumns}
                  getKey={(row) => row.sourceId}
                  selectedKeys={selectedKeys}
                  onSelect={handleSelectCandidates}
                  enableMultiSelect
                  virtualization={{ enable: true }}
                  className="h-96"
                  styles={{ size: 'sm' }}
                />
              </div>
            )}
          </AwaitContainer>
        )}
      </CardContent>

      <CardContent className="flex flex-col gap-3">
        <p className="font-medium">{t('integrations:c1Bot.journal.title')}</p>
        <AwaitContainer isLoading={isLoading || isFetching}>
          {items.length === 0 ? (
            <div className="text-muted-foreground p-2">{t('integrations:c1Bot.table.empty')}</div>
          ) : (
            <Table
              data={items}
              columns={journalColumns}
              getKey={(row) => row.sourceId}
              pagination={{ total, page, perPage: PER_PAGE }}
              type="paginated"
              onChangePagination={handleChangePagination}
              styles={{ size: 'sm' }}
            />
          )}
        </AwaitContainer>
      </CardContent>
    </Card>
  );
};
